import {
  tags,
  unwrapDeepRefs,
  type DeepMaybeRef,
  type MaybeRef,
} from "@/api/common";
import { useLocationSelector } from "@/composables/location";
import { endpoints } from "@/endpoints";
import useApiStore from "@/stores/api";
import { useQueries, useQuery } from "@tanstack/vue-query";
import axios from "axios";
import { computed, unref, type Ref } from "vue";
import { queryKeys } from "../queryKeys";
import {
  type VisitApiResponse,
  type Visit,
  type ListVisitsQuery,
  visitKeys,
  visitFieldKeys,
  type VisitTrendsParams,
  type VisitTrendsResponse,
} from ".";
import type { VisitorField } from "../visitorFields";
import { useNotification } from "@/composables/useNotification";
import { useI18n } from "vue-i18n";
import {
  ApiClient,
  type ListVisitFieldsItemPagedApiResponse,
  type ListVisitItemPagedApiResponse,
} from "../zoolander";
import { useDateUtils } from "@/composables/dates";
import { HOUR_IN_MS } from "@/stores/constants";

export const useListVisits = (
  filters: DeepMaybeRef<ListVisitsQuery>,
  onSuccess?: (data: ListVisitItemPagedApiResponse) => void,
  enabled?: MaybeRef<boolean>
) => {
  const { parseNaiveDateForRequest, parseDateFromResponse } = useDateUtils();
  const { isLocationsLoaded, locationParamZoolander } = useLocationSelector();
  const locationGroupId = computed(
    () => locationParamZoolander.value.locationGroupId
  );
  const locationId = computed(() => locationParamZoolander.value.locationId);

  const mergedFilters = computed(() => {
    const unwrappedFilters = unwrapDeepRefs(filters);
    return {
      locationId: locationId.value,
      locationGroupId: locationGroupId.value,
      expand: "host,location",
      ...unwrappedFilters,
      startedAt: parseNaiveDateForRequest(unwrappedFilters?.startedAt as Date),
      finishedAt: parseNaiveDateForRequest(
        unwrappedFilters?.finishedAt as Date
      ),
    };
  });

  return useQuery({
    queryKey: visitKeys.list(mergedFilters).queryKey,
    queryFn: () => new ApiClient().listVisits(unwrapDeepRefs(mergedFilters)),
    enabled: computed(
      () => isLocationsLoaded.value && unref(enabled) !== false
    ),
    onSuccess,
    keepPreviousData: true,
    select: (response) => {
      const data = response.data.map((item) => ({
        ...item,
        startedAt: parseDateFromResponse(
          item.startedAt,
          item?.location?.timeZone
        ),
        finishedAt: item.finishedAt
          ? parseDateFromResponse(item.finishedAt, item?.location?.timeZone)
          : undefined,
        reviewedAt: item.reviewedAt
          ? parseDateFromResponse(item.reviewedAt, item?.location?.timeZone)
          : undefined,
      }));

      return {
        ...response,
        data,
      };
    },
  });
};

export const useCountVisits = (
  filters: DeepMaybeRef<ListVisitsQuery>,
  enabled?: MaybeRef<boolean>
) => {
  const { parseNaiveDateForRequest } = useDateUtils();
  const { isLocationsLoaded, locationParamZoolander } = useLocationSelector();
  const locationGroupId = computed(
    () => locationParamZoolander.value.locationGroupId
  );
  const locationId = computed(() => locationParamZoolander.value.locationId);
  const isEnabled = computed(
    () => isLocationsLoaded.value && unref(enabled) !== false
  );

  const mergedFilters = computed(() => {
    const unwrappedFilters = unwrapDeepRefs(filters);
    return {
      locationId: locationId.value,
      locationGroupId: locationGroupId.value,
      expand: "host,location",
      ...unwrappedFilters,
      startedAt: parseNaiveDateForRequest(unwrappedFilters?.startedAt as Date),
      finishedAt: parseNaiveDateForRequest(
        unwrappedFilters?.finishedAt as Date
      ),
    };
  });

  return useQuery({
    queryKey: visitKeys.count(mergedFilters).queryKey,
    queryFn: () => new ApiClient().countVisits(unwrapDeepRefs(mergedFilters)),
    enabled: isEnabled,
  });
};

export const useVisitTrends = () => {
  const apiStore = useApiStore();
  const { locationParams, isLocationsLoaded } = useLocationSelector();
  const { sendErrorNotification } = useNotification();
  const { t } = useI18n();

  return useQuery({
    queryKey: queryKeys[tags.platform.visits].trends(locationParams).queryKey,
    queryFn: async () => {
      const params: VisitTrendsParams = {
        ...unwrapDeepRefs(locationParams),
      };

      const result = await axios.get<VisitTrendsResponse>(
        apiStore.getEndpoint("platform", endpoints.visits.trends),
        { params }
      );
      return result.data;
    },
    onError: () => sendErrorNotification(t("errors.fetchVisitsFailed"), true),
    select: (res) => res?.data,
    keepPreviousData: true,
    retry: false,
    cacheTime: 60000,
    enabled: isLocationsLoaded,
  });
};

export const useGetVisit = (visitId: MaybeRef<string>) => {
  const { isLocationsLoaded } = useLocationSelector();
  const { parseDateFromResponse } = useDateUtils();
  const enabled = computed(() => isLocationsLoaded && !!unref(visitId));
  return useQuery({
    queryKey: visitKeys.byId(visitId).queryKey,
    queryFn: async () => {
      const response = await new ApiClient().getVisit({
        id: unref(visitId),
        expand: "host,location",
      });
      if (response.data.anonymizedAt) {
        return {
          ...response,
          data: {
            ...response.data,
            fields: [],
          },
        };
      }
      const fieldsResponse = await new ApiClient().getVisitFields({
        id: response.data.id,
      });
      return {
        ...response,
        data: {
          ...response.data,
          fields: fieldsResponse.data,
        },
      };
    },
    refetchOnWindowFocus: false,
    enabled,
    select: (response) => ({
      ...response.data,
      startedAt: parseDateFromResponse(
        response?.data.startedAt,
        response.data.location?.timeZone
      ),
      finishedAt: response?.data.finishedAt
        ? parseDateFromResponse(
            response?.data.finishedAt,
            response.data.location?.timeZone
          )
        : undefined,
      reviewedAt: response?.data.reviewedAt
        ? parseDateFromResponse(
            response?.data.reviewedAt,
            response.data.location?.timeZone
          )
        : undefined,
    }),
  });
};

export const mapVisit = (visit: VisitApiResponse): Visit => {
  return {
    ...visit,
    id: visit.pk,
    numericId: visit.id,
    customFields: visit.customFields.map(
      (field) =>
        ({
          id: field.id,
          pk: field.pk,
          fieldType: field.field_type,
          fieldOptions: field.field_options,
          name: field.name,
          order: field.order,
          enabled: stringToBoolean(field.is_enabled),
          required: stringToBoolean(field.is_required),
          temporary: stringToBoolean(field.is_temporary),
          settings: field.settings,
          metadata: field.metadata,
          value: field.value,
          label: field.label,
        }) as VisitorField
    ),
  };
};

const stringToBoolean = (value: string | undefined | null) => {
  return Boolean(value && value === "1");
};

const visitFieldsQuery = (
  visitId: MaybeRef<string>,
  enabled: Ref<boolean>
) => ({
  queryKey: visitFieldKeys.byVisitId(visitId).queryKey,
  queryFn: () => new ApiClient().getVisitFields({ id: unref(visitId) }),
  enabled,
  staleTime: HOUR_IN_MS,
  select: (response: ListVisitFieldsItemPagedApiResponse) => ({
    ...response,
    visitId: unref(visitId),
  }),
});

export const useGetVisitFields = (
  visitIds: Ref<string[]>,
  enabled?: Ref<boolean>
) => {
  const { isLocationsLoaded } = useLocationSelector();

  return useQueries({
    queries: computed(() =>
      visitIds.value.map((id) =>
        visitFieldsQuery(
          id,
          computed(
            () =>
              enabled?.value !== false &&
              isLocationsLoaded.value &&
              visitIds.value.length > 0
          )
        )
      )
    ),
  });
};
